import { Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { autoRenewMessages, subscriptionBannerMessages } from '@app/configs/app-message.config';
import { CommonService } from '@app/core/services/common.service';
import { StripePaymentService } from '@app/modules/account/service/stripe.service';
import {
  AutoRenewIneligibleReason,
  canChangeAutoRenew,
  getDaysRemaining,
  SUBSCRIPTION_DATE_FORMAT,
  SubscriptionInfo
} from '@app/shared/models/subscription-info.model';
import { SavedCard } from '@app/shared/models/subscription-renewal.model';
import { SharedModalService } from '@app/shared/services/shared-modal.service';
import { SubscriptionStateService } from '@app/shared/services/subscription-state.service';
import { getUpgradeSubscriptionRoute } from '@app/shared/subscription-routes';

/** The part of a Material slide toggle change event this component uses. */
export interface AutoRenewChange {
  checked: boolean;
  source?: { checked: boolean };
}

let nextAutoRenewId = 0;

@Component({
  selector: 'app-subscription-status',
  templateUrl: './subscription-status.component.html',
  styleUrls: ['./subscription-status.component.scss']
})
export class SubscriptionStatusComponent implements OnInit, DoCheck, OnDestroy {
  @Input() subscription: SubscriptionInfo | null = null;
  /** `autorenew` shows only the Auto-Renewal panel, for the Subscription & Payment tab. */
  @Input() mode: 'banner' | 'card' | 'autorenew' = 'card';
  /** Customer-facing plan name, for example Premium Plan. */
  @Input() planLabel: string | null = null;
  @Input() userType: number = parseInt(localStorage.getItem('userType'), 10);
  @Input() showActionButton = true;
  /** Emitted by the Auto-Renewal Scheduled banner so the page can open the Subscription & Payment tab. */
  @Output() viewSubscription = new EventEmitter<void>();

  readonly messages = autoRenewMessages;
  /** Saved card shown in the Scheduled banner; null until it loads or when there is none. */
  savedCard: SavedCard | null = null;
  private savedCardRequested = false;

  /** Turns true while a change is being saved, so the setting cannot be submitted twice. */
  autoRenewSaving = false;
  /** Turns true while the confirmation popup is open, so a second change cannot start behind it. */
  autoRenewConfirming = false;
  /** Result of the last change, read out by screen readers. */
  autoRenewAnnouncement = '';
  readonly autoRenewLabelId = `subscription-auto-renew-label-${nextAutoRenewId++}`;
  readonly dateFormat = SUBSCRIPTION_DATE_FORMAT;

  constructor(
    private _router: Router,
    private stripeService: StripePaymentService,
    private commonService: CommonService,
    private sharedModalService: SharedModalService,
    private subscriptionState: SubscriptionStateService
  ) { }

  private cardUpdates: Subscription;

  ngOnInit(): void {
    // The banner names the saved card, so it follows the card when the owner replaces it in the Subscription & Payment tab.
    this.cardUpdates = this.subscriptionState.savedCardUpdated$.subscribe((card) => (this.savedCard = card));
  }

  ngOnDestroy(): void {
    if (this.cardUpdates) {
      this.cardUpdates.unsubscribe();
    }
  }

  /** Loads the saved card once, the first time the Scheduled banner is due (also when Auto-Renewal is turned on in place). */
  ngDoCheck(): void {
    if (this.showScheduledBanner && !this.savedCardRequested) {
      this.savedCardRequested = true;
      this.stripeService.getSavedCard(localStorage.getItem('userToken')).subscribe(
        (res) => {
          this.savedCard = res && Number(res.statusCode) === 200 && res.data ? res.data : null;
        },
        () => (this.savedCard = null)
      );
    }
  }

  /** Renew is offered only when the subscription is Expiring Soon or Expired. */
  get canShowRenew(): boolean {
    return !!this.subscription && this.subscription.canRenew && this.subscription.status !== 'active';
  }

  get showBanner(): boolean {
    return this.mode === 'banner' && this.canShowRenew && !this.showScheduledBanner;
  }

  /** Shown in the same cases as the Renew banner (Expiring Soon), but when Auto-Renewal is on: says when and from which card it renews. */
  get showScheduledBanner(): boolean {
    return (
      this.mode === 'banner' &&
      !!this.subscription &&
      this.subscription.autoRenew === true &&
      this.subscription.status === 'expiringSoon' &&
      !!this.subscription.expiryDate &&
      !isNaN(new Date(this.subscription.expiryDate).getTime())
    );
  }

  /** "in 26 days, on" before the date; just "on" when the days left are unknown. */
  get scheduledLead(): string {
    const days = this.daysRemaining;
    return days === null ? 'on' : `in ${days} ${days === 1 ? 'day' : 'days'}, on`;
  }

  /** Brand and last four digits only, for example "Visa •••• 4242"; empty when the card is not known. */
  get savedCardText(): string {
    return this.savedCard ? `${this.savedCard.brand} •••• ${this.savedCard.last4}` : '';
  }

  /** The status box, in the same place for Active, Expiring Soon and Expired; only the colours and the text change. */
  get showCard(): boolean {
    return this.mode === 'card' && !!this.subscription;
  }

  get isExpired(): boolean {
    return !!this.subscription && this.subscription.status === 'expired';
  }

  /** The Auto-Renew row is shown only when the backend sent a value, and only on a live (not expired) card. */
  get showAutoRenew(): boolean {
    return this.showCard && !this.isExpired && typeof this.subscription.autoRenew === 'boolean';
  }

  /** The Auto-Renewal panel of the tab: same rules as the card's row, without the card around it. */
  get showAutoRenewPanel(): boolean {
    return (
      this.mode === 'autorenew' &&
      !!this.subscription &&
      !this.isExpired &&
      typeof this.subscription.autoRenew === 'boolean'
    );
  }


  /** Owners get a toggle; every other role sees the status as text. */
  get canChangeAutoRenew(): boolean {
    return canChangeAutoRenew(this.userType);
  }

  /** The sentence under the Auto-Renewal heading; it carries the state in words, not only in the toggle. */
  get autoRenewStateText(): string {
    return this.subscription && this.subscription.autoRenew ? autoRenewMessages.stateOn : autoRenewMessages.stateOff;
  }

  /** The charge note is shown while Auto-Renewal is on and the date it applies to (the expiry date) is readable. */
  get showAutoRenewCharge(): boolean {
    return (
      !!this.subscription &&
      this.subscription.autoRenew === true &&
      !!this.subscription.expiryDate &&
      !isNaN(new Date(this.subscription.expiryDate).getTime())
    );
  }

  /** Off and not eligible: it cannot be turned on. An enabled setting can always be turned off. */
  get autoRenewBlocked(): boolean {
    return !!this.subscription && !this.subscription.autoRenew && this.subscription.autoRenewEligible === false;
  }

  get autoRenewToggleDisabled(): boolean {
    return this.autoRenewSaving || this.autoRenewConfirming || this.autoRenewBlocked;
  }

  /** Why Auto-Renew cannot be turned on, for an owner who sees it blocked. */
  get autoRenewReason(): string | null {
    if (!this.autoRenewBlocked) {
      return null;
    }
    const reasons = autoRenewMessages.reasons;
    const reason: AutoRenewIneligibleReason | null = this.subscription.autoRenewIneligibleReason || null;
    return (reason && reasons[reason]) || reasons.unknown;
  }

  get daysRemaining(): number | null {
    return getDaysRemaining(this.subscription);
  }

  /** The days-left text is shown only while the subscription is Expiring Soon. */
  get showDaysLeft(): boolean {
    return !!this.subscription && this.subscription.status === 'expiringSoon' && this.daysRemaining !== null;
  }

  get statusLabel(): string {
    switch (this.subscription && this.subscription.status) {
      case 'expired':
        return 'Subscription Expired';
      case 'expiringSoon':
        return 'Expiring Soon';
      default:
        return 'Active';
    }
  }

  /** The chip in the status box: "Expired" here, while the banner title keeps "Subscription Expired". */
  get cardStatusLabel(): string {
    return this.isExpired ? 'Expired' : this.statusLabel;
  }

  /** Before the expiry date the box says "Expires on", after it "Expired on". */
  get expiryPrefix(): string {
    return this.isExpired ? 'Expired on' : 'Expires on';
  }

  get bannerMessage(): string {
    if (this.isExpired) {
      return subscriptionBannerMessages.expired;
    }
    const days = this.daysRemaining;
    const when = days === null ? 'is about to expire' : `expires in ${days} ${days === 1 ? 'day' : 'days'}`;
    return subscriptionBannerMessages.expiringSoon(when);
  }

  onRenew(): void {
    const route = getUpgradeSubscriptionRoute(this.userType);
    if (route) {
      this._router.navigate([route]);
    }
  }

  /**
   * Asks the owner to confirm turning Auto-Renewal on or off, and saves the choice only on Yes. The toggle has already
   * flipped when this runs, so it is put straight back and stays at the saved position while the popup is open; it moves
   * to the new position only on Yes. No (or closing the popup) leaves it where it is. A refused or failed save puts it
   * back and tells the owner why. A second change is ignored while one is being confirmed or saved.
   */
  onAutoRenewChange(change: AutoRenewChange): void {
    if (!this.subscription || this.autoRenewSaving || this.autoRenewConfirming) {
      return;
    }
    const previous = !!this.subscription.autoRenew;
    if (!this.canChangeAutoRenew || (change.checked && this.autoRenewBlocked)) {
      this.restoreAutoRenew(change, previous);
      return;
    }

    // Material flips the toggle on click; undo that until the owner confirms, so it never moves on No.
    this.restoreAutoRenew(change, previous);
    // Busy from now on, also while the card is read for the turn-on pop-up, so a second change is ignored.
    this.autoRenewConfirming = true;
    if (change.checked) {
      this.readSavedCardLast4().subscribe((last4) => this.confirmAutoRenew(change, previous, this.enableConfirmation(last4)));
    } else {
      this.confirmAutoRenew(change, previous, { title: autoRenewMessages.confirmDisableTitle, text: autoRenewMessages.confirmDisable });
    }
  }

  private confirmAutoRenew(change: AutoRenewChange, previous: boolean, popup: { title: string; text: string }): void {
    this.sharedModalService.confirmDecision(popup.title, popup.text).pipe(take(1)).subscribe((confirmed) => {
      this.autoRenewConfirming = false;
      if (confirmed) {
        if (change.source) {
          change.source.checked = change.checked;
        }
        this.saveAutoRenew(change, previous);
      }
    });
  }

  /** The last four digits of the owner's own saved card, or null when they cannot be read; never throws or shows an error. */
  private readSavedCardLast4(): Observable<string | null> {
    return this.stripeService.getSavedCard(localStorage.getItem('userToken')).pipe(
      take(1),
      map((res) => {
        const last4 = res && Number(res.statusCode) === 200 && res.data ? String(res.data.last4) : '';
        return /^\d{4}$/.test(last4) ? last4 : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * The turn-on pop-up: one sentence with the renewal date and the saved card. Without a readable renewal date it
   * keeps the plain question, so nothing wrong is ever shown. The description is written as HTML, so it is built only
   * from the formatted date and exactly four digits.
   */
  private enableConfirmation(last4: string | null): { title: string; text: string } {
    const expiry = this.subscription && this.subscription.expiryDate;
    if (!expiry || isNaN(new Date(expiry).getTime())) {
      return { title: autoRenewMessages.confirmTitle, text: autoRenewMessages.confirmEnable };
    }
    const date = formatDate(expiry, SUBSCRIPTION_DATE_FORMAT, 'en-US', 'UTC');
    return { title: autoRenewMessages.confirmEnableTitle, text: autoRenewMessages.confirmEnableSentence(date, last4) };
  }

  private saveAutoRenew(change: AutoRenewChange, previous: boolean): void {
    this.autoRenewSaving = true;
    this.stripeService.setAutoRenew(change.checked, localStorage.getItem('userToken')).subscribe(
      (res) => {
        this.autoRenewSaving = false;
        if (res && (Number(res.statusCode) === 200 || Number(res.statusCode) === 201)) {
          this.onAutoRenewSaved(res.data && typeof res.data.autoRenew === 'boolean' ? res.data.autoRenew : change.checked);
        } else {
          if (res && res.data && res.data.reason) {
            this.subscription.autoRenewEligible = false;
            this.subscription.autoRenewIneligibleReason = res.data.reason;
          }
          this.onAutoRenewFailed(change, previous, res && res.message);
        }
      },
      (error) => {
        this.autoRenewSaving = false;
        this.onAutoRenewFailed(change, previous, error && error.error && error.error.message);
      }
    );
  }

  private onAutoRenewSaved(enabled: boolean): void {
    this.subscription.autoRenew = enabled;
    this.autoRenewAnnouncement = enabled ? autoRenewMessages.enabled : autoRenewMessages.disabled;
    this.commonService.showSuccessSnakeBar(this.autoRenewAnnouncement);
  }

  private onAutoRenewFailed(change: AutoRenewChange, previous: boolean, message?: string): void {
    this.restoreAutoRenew(change, previous);
    this.autoRenewAnnouncement = message || autoRenewMessages.failure;
    this.commonService.showErrorSnakeBar(this.autoRenewAnnouncement);
  }

  private restoreAutoRenew(change: AutoRenewChange, previous: boolean): void {
    this.subscription.autoRenew = previous;
    if (change.source) {
      change.source.checked = previous;
    }
  }
}
